import { File } from "lucide-react";
import { catColor } from "./constants";

const StepPreview = ({ template, group, bodyParams, metaBody, broadcastName, onNameChange }) => {
  const previewBody = metaBody
    ? bodyParams.reduce(
        (t, v, i) => t.replace(new RegExp(`\\{\\{${i + 1}\\}\\}`, "g"), v || `{{${i + 1}}}`),
        metaBody
      )
    : null;

  const BASE_URL = process.env.REACT_APP_BASE_URL || "";
  const headerMediaUrl =
    template?.headerValue && ["image", "video", "document"].includes(template?.headerType)
      ? `${BASE_URL}${template.headerValue}`
      : null;

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      {/* Left: summary */}
      <div className="flex-1 space-y-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-1">Preview & Confirm</h2>
          <p className="text-sm text-gray-500">Review your broadcast before sending.</p>
        </div>

        {/* Campaign name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Campaign Name <span className="text-gray-400 font-normal">(optional)</span>
          </label>
          <input
            value={broadcastName}
            onChange={(e) => onNameChange(e.target.value)}
            placeholder="e.g. May Discount Campaign"
            className="w-full text-sm border border-gray-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
          />
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-gray-50 rounded-xl p-3 border border-gray-100">
            <p className="text-xs text-gray-400 mb-1">Audience</p>
            <p className="text-sm font-semibold text-gray-800 truncate">{group?.name}</p>
            <p className="text-[11px] text-gray-500 mt-1">
              ~{group?.activeCustomerCount ?? "?"} recipients
            </p>
          </div>
          <div className="bg-gray-50 rounded-xl p-3 border border-gray-100">
            <p className="text-xs text-gray-400 mb-1">Template</p>
            <p className="text-sm font-semibold text-gray-800 truncate">{template?.templateName}</p>
            <span className={`inline-block text-[10px] font-medium px-1.5 py-0.5 rounded mt-1 ${catColor(template?.category)}`}>
              {template?.category}
            </span>
          </div>
        </div>

        {/* Filled variables summary */}
        {bodyParams.length > 0 && (
          <div className="bg-gray-50 border border-gray-100 rounded-xl p-3">
            <p className="text-xs font-medium text-gray-500 mb-2">Variables</p>
            <div className="space-y-1">
              {bodyParams.map((v, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="text-[11px] text-gray-400 font-mono w-10 flex-shrink-0">{`{{${i + 1}}}`}</span>
                  <span className="text-xs text-gray-700 truncate">{v || <em className="text-gray-400">empty</em>}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Right: WhatsApp preview */}
      <div className="lg:w-72 flex-shrink-0">
        <p className="text-xs font-medium text-gray-500 mb-2 uppercase tracking-wide">WhatsApp Preview</p>
        <div className="bg-[#e5ddd5] rounded-2xl p-3">
          <div className="bg-white rounded-xl overflow-hidden shadow-sm max-w-[260px]">
            {headerMediaUrl && template?.headerType === "image" && (
              <img src={headerMediaUrl} alt="header" className="w-full max-h-36 object-cover" />
            )}
            {headerMediaUrl && template?.headerType === "video" && (
              <video src={headerMediaUrl} className="w-full max-h-36 object-cover bg-black" muted preload="metadata" />
            )}
            {headerMediaUrl && template?.headerType === "document" && (
              <div className="flex items-center gap-2 bg-gray-100 px-3 py-2">
                <File className="w-4 h-4 text-gray-500" />
                <span className="text-xs text-gray-600 truncate">{template.headerValue?.split("/").pop()}</span>
              </div>
            )}
            {template?.headerType === "text" && template?.headerValue && (
              <p className="text-sm font-semibold px-3 pt-3">{template.headerValue}</p>
            )}
            <div className="px-3 py-2 space-y-1">
              {previewBody ? (
                <p className="text-sm text-gray-800 whitespace-pre-wrap break-words leading-relaxed">{previewBody}</p>
              ) : (
                <p className="text-xs text-gray-400 italic">Body text loading…</p>
              )}
              {template?.footerText && (
                <p className="text-[11px] text-gray-400">{template.footerText}</p>
              )}
            </div>
            <div className="border-t border-gray-100 px-3 py-1.5 text-right">
              <span className="text-[10px] text-gray-400">
                {new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StepPreview;
